import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser, useFirestore } from 'reactfire';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  orderBy,
  query,
  Timestamp,
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import Button, { BUTTON_TYPE, BUTTON_MODE } from '@components/Button';
import Loader from '@components/Loader';
import { usePrediction } from '@hooks/usePrediction';
import { ROUTES } from '@routes/routes';
import FeatherIcon from 'feather-icons-react';
import styles from './styles.module.scss';

const TTS_FUNCTION_URL = process.env.REACT_APP_TTS_FUNCTION_URL;
const MAX_SAVED_AUDIOS = 10;
const MAX_TEXT_LENGTH = 500;

type SavedAudio = {
  id: string;
  label: string;
  storagePath: string;
  createdAt: Timestamp;
};

export default function Speak() {
  const { t, i18n } = useTranslation('speak');
  const navigate = useNavigate();
  const db = useFirestore();
  const { data: user } = useUser();

  const [isPageLoading, setIsPageLoading] = React.useState(true);
  const [text, setText] = React.useState('');
  const [speed, setSpeed] = React.useState(1.0);
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [error, setError] = React.useState('');
  const [modelId, setModelId] = React.useState<string | null>(null);

  const SPEED_MIN = 0.5;
  const SPEED_MAX = 1.5;
  const SPEED_STEP = 0.1;
  const speedRef = React.useRef(speed);

  const audioRef = React.useRef<HTMLAudioElement>(null);

  React.useEffect(() => {
    speedRef.current = speed;
    if (audioRef.current) {
      audioRef.current.defaultPlaybackRate = speed;
      audioRef.current.playbackRate = speed;
    }
  }, [speed]);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [hasAudio, setHasAudio] = React.useState(false);
  const lastBlobRef = React.useRef<Blob | null>(null);

  // Word prediction
  const { suggestions, selectSuggestion } = usePrediction(text, i18n.language);

  const handleSelectSuggestion = (word: string) => {
    setText(selectSuggestion(word));
    textareaRef.current?.focus();
  };

  // Virtual keyboard state
  const [showKeyboard, setShowKeyboard] = React.useState(false);
  const [showSettings, setShowSettings] = React.useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  // Dwell-click state
  const [dwellEnabled, setDwellEnabled] = React.useState(false);
  const [dwellDuration, setDwellDuration] = React.useState(800);
  const [dwellingKey, setDwellingKey] = React.useState<string | null>(null);
  const dwellTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const shareBtnRef = React.useRef<HTMLButtonElement>(null);

  const startContinuousScroll = (direction: 'up' | 'down') => {
    const SCROLL_AMOUNT = 160;
    const PAUSE_MS = 700;
    const doScroll = () => {
      window.scrollBy({ top: direction === 'up' ? -SCROLL_AMOUNT : SCROLL_AMOUNT, behavior: 'smooth' });
      scrollTimerRef.current = setTimeout(doScroll, PAUSE_MS);
    };
    doScroll();
  };

  const stopContinuousScroll = () => {
    if (scrollTimerRef.current) {
      clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = null;
    }
  };

  const keyboardRows = [
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
    ['Z', 'X', 'C', 'V', 'B', 'N', 'M'],
  ];

  const handleKeyPress = (key: string) => {
    setText((prev) => prev + key.toLowerCase());
    textareaRef.current?.focus();
  };

  const handleBackspace = () => {
    setText((prev) => prev.slice(0, -1));
    textareaRef.current?.focus();
  };

  const handleSpace = () => {
    setText((prev) => prev + ' ');
    textareaRef.current?.focus();
  };

  const handlePunctuation = (char: string) => {
    setText((prev) => prev + char);
    textareaRef.current?.focus();
  };

  const handleClear = () => {
    setText('');
    textareaRef.current?.focus();
  };


  const startDwell = (keyId: string, action: () => void) => {
    if (!dwellEnabled) return;
    cancelDwell();
    setDwellingKey(keyId);
    dwellTimerRef.current = setTimeout(() => {
      action();
      setDwellingKey(null);
      dwellTimerRef.current = null;
    }, dwellDuration);
  };

  const cancelDwell = () => {
    if (dwellTimerRef.current) {
      clearTimeout(dwellTimerRef.current);
      dwellTimerRef.current = null;
    }
    setDwellingKey(null);
  };

  // Clean up dwell timer on unmount or when dwell is disabled
  React.useEffect(() => {
    if (!dwellEnabled) {
      cancelDwell();
    }
    return () => {
      if (dwellTimerRef.current) {
        clearTimeout(dwellTimerRef.current);
      }
      stopContinuousScroll();
    };
  }, [dwellEnabled]);

  // Keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = async (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;

      if (e.key === 'k' || e.key === 'K') {
        e.preventDefault();
        try {
          const clipText = await navigator.clipboard.readText();
          if (clipText) setText(clipText);
          textareaRef.current?.focus();
        } catch {
          // Clipboard access denied
        }
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (!isGenerating && text.trim()) handleGenerate();
      } else if (e.key === '-') {
        e.preventDefault();
        if (hasAudio) {
          if (isPlaying) handleStop();
          else handleReplay();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isGenerating, text, hasAudio, isPlaying, modelId]);

  const saveSpeakSettings = React.useCallback(
    async (settings: Record<string, boolean | number>) => {
      if (!user) return;
      try {
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, settings);
      } catch {
        // Silently fail — settings are not critical
      }
    },
    [user, db]
  );

  const handleDwellToggle = () => {
    const newEnabled = !dwellEnabled;
    setDwellEnabled(newEnabled);
    saveSpeakSettings({ dwellEnabled: newEnabled });
  };

  const settingsSaveTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    return () => {
      if (settingsSaveTimerRef.current) {
        clearTimeout(settingsSaveTimerRef.current);
        settingsSaveTimerRef.current = null;
      }
    };
  }, []);

  const debouncedSave = (settings: Record<string, boolean | number>) => {
    if (settingsSaveTimerRef.current) {
      clearTimeout(settingsSaveTimerRef.current);
    }
    settingsSaveTimerRef.current = setTimeout(() => {
      saveSpeakSettings(settings);
    }, 500);
  };

  const handleDwellDurationChange = (newDuration: number) => {
    setDwellDuration(newDuration);
    debouncedSave({ dwellDuration: newDuration });
  };

  const handleSpeedChange = (newSpeed: number) => {
    setSpeed(newSpeed);
    debouncedSave({ speakSpeed: newSpeed });
  };

  // Saved audios state
  const [savedAudios, setSavedAudios] = React.useState<SavedAudio[]>([]);
  const [isSaving, setIsSaving] = React.useState(false);
  const [playingSavedId, setPlayingSavedId] = React.useState<string | null>(null);

  React.useEffect(() => {
    async function fetchUserData() {
      if (!user) return;
      const userRef = doc(db, 'users', user.uid);
      const docSnap = await getDoc(userRef);
      if (docSnap.exists()) {
        const userData = docSnap.data();
        if (!userData?.voiceEnabled) {
          navigate(ROUTES.BASE);
          return;
        }
        if (typeof userData.dwellEnabled === 'boolean') {
          setDwellEnabled(userData.dwellEnabled);
        }
        if (typeof userData.dwellDuration === 'number') {
          setDwellDuration(userData.dwellDuration);
        }
        if (typeof userData.speakSpeed === 'number') {
          setSpeed(userData.speakSpeed);
        } else if (typeof userData.speekSpeed === 'number') {
          setSpeed(userData.speekSpeed);
        }
        if (userData.model_id) {
          setModelId(userData.model_id);
        }
      }
      setIsPageLoading(false);
    }
    fetchUserData();
  }, [user, db, navigate]);

  // Load saved audios
  React.useEffect(() => {
    async function loadSavedAudios() {
      if (!user) return;
      const colRef = collection(db, `users/${user.uid}/savedAudios`);
      const q = query(colRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const audios: SavedAudio[] = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as SavedAudio[];
      setSavedAudios(audios);
    }
    if (!isPageLoading) {
      loadSavedAudios();
    }
  }, [user, db, isPageLoading]);

  const playAudio = async (url: string) => {
    if (!audioRef.current) return;
    const audio = audioRef.current;
    const rate = speedRef.current;
    audio.defaultPlaybackRate = rate;
    audio.src = url;
    audio.load();
    await audio.play();
    audio.playbackRate = rate;
    setIsPlaying(true);
    setHasAudio(true);
  };

  const handleGenerate = async () => {
    if (!text.trim()) return;
    if (!TTS_FUNCTION_URL || !modelId) {
      setError(t('noCredentials'));
      return;
    }
    const trimmed = text.trim();
    if (trimmed.length > MAX_TEXT_LENGTH) {
      setError(t('maxTextLength', { max: MAX_TEXT_LENGTH }));
      return;
    }

    setError('');
    setIsGenerating(true);
    setHasAudio(false);
    setPlayingSavedId(null);
    handleStop();

    try {
      const idToken = await user?.getIdToken();
      const response = await fetch(TTS_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          text: trimmed,
          model_id: modelId,
        }),
      });

      if (!response.ok) {
        throw new Error('TTS request failed');
      }

      const blob = await response.blob();
      lastBlobRef.current = blob;

      const previousSrc = audioRef.current?.src;
      if (previousSrc && previousSrc.startsWith('blob:')) {
        URL.revokeObjectURL(previousSrc);
      }

      const url = URL.createObjectURL(blob);
      await playAudio(url);
    } catch (err) {
      setError(t('error'));
    } finally {
      setIsGenerating(false);
      textareaRef.current?.focus();
    }
  };

  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const handleReplay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = 0;
    audio.playbackRate = speedRef.current;

    audio
      .play()
      .then(() => {
        audio.playbackRate = speedRef.current;
        setIsPlaying(true);
      })
      .catch(() => {
        setIsPlaying(false);
        setError(t('error'));
      });
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setPlayingSavedId(null);
  };

  const handleDownload = () => {
    if (!lastBlobRef.current) return;
    const safeBaseName = (text.trim().slice(0, 40) || 'audio').replace(/[\\/:*?"<>|]+/g, '_');
    const fileName = `${safeBaseName}.mp3`;
    const url = URL.createObjectURL(lastBlobRef.current);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleShare = async () => {
    if (!lastBlobRef.current) return;
    const safeBaseName = (text.trim().slice(0, 40) || 'audio').replace(/[\\/:*?"<>|]+/g, '_');
    const fileName = `${safeBaseName}.mp3`;
    const file = new File([lastBlobRef.current], fileName, { type: 'audio/mpeg' });

    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: text.trim() });
      } else {
        handleDownload();
      }
    } catch (err: any) {
      if (err?.name === 'AbortError' || err?.name === 'InvalidStateError') return;
      if (err?.name === 'NotAllowedError') {
        handleDownload();
        return;
      }
    }
  };

  const handleSave = async () => {
    if (!user || !lastBlobRef.current) return;
    if (savedAudios.length >= MAX_SAVED_AUDIOS) return;

    setIsSaving(true);
    try {
      const storage = getStorage();
      const fileName = `${Date.now()}.mp3`;
      const storagePath = `saved-audios/${user.uid}/${fileName}`;
      const storageRef = ref(storage, storagePath);

      await uploadBytes(storageRef, lastBlobRef.current, {
        contentType: 'audio/mpeg',
      });

      const docRef = await addDoc(
        collection(db, `users/${user.uid}/savedAudios`),
        {
          label: text.trim(),
          storagePath,
          createdAt: Timestamp.now(),
        }
      );

      setSavedAudios((prev) => [
        {
          id: docRef.id,
          label: text.trim(),
          storagePath,
          createdAt: Timestamp.now(),
        },
        ...prev,
      ]);
    } catch (err) {
      setError(t('error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSaved = async (audio: SavedAudio) => {
    if (!user) return;
    try {
      const storage = getStorage();
      const storageRef = ref(storage, audio.storagePath);
      await deleteObject(storageRef);
      await deleteDoc(doc(db, `users/${user.uid}/savedAudios`, audio.id));
      setSavedAudios((prev) => prev.filter((a) => a.id !== audio.id));
      if (playingSavedId === audio.id) {
        handleStop();
        setPlayingSavedId(null);
      }
    } catch (err) {
      setError(t('error'));
    }
  };

  const handlePlaySaved = async (audio: SavedAudio) => {
    if (playingSavedId === audio.id && isPlaying) {
      handleStop();
      setPlayingSavedId(null);
      return;
    }
    try {
      const storage = getStorage();
      const storageRef = ref(storage, audio.storagePath);
      const url = await getDownloadURL(storageRef);
      setPlayingSavedId(audio.id);
      await playAudio(url);
    } catch (err) {
      setError(t('error'));
    }
  };

  if (isPageLoading) {
    return <Loader />;
  }

  return (
    <>
      <Helmet>
        <title>{t('pageTitle')}</title>
        <meta name="description" content={t('descriptionPage')} />
      </Helmet>

      <div className={styles.container}>
        <div className={styles.wrapper}>
          <div
            className={`${styles.dwellWrap} ${dwellingKey === 'TEXTAREA' ? styles.dwelling : ''}`}
            style={{ '--dwell-duration': `${dwellDuration}ms` } as React.CSSProperties}
            onPointerEnter={() => dwellEnabled && startDwell('TEXTAREA', () => textareaRef.current?.focus())}
            onPointerLeave={cancelDwell}
          >
            <textarea
              ref={textareaRef}
              className={styles.textarea}
              placeholder={t('placeholder')}
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={isGenerating}
              maxLength={MAX_TEXT_LENGTH}
            />
          </div>

          <div className={`${styles.charCount} ${text.length > MAX_TEXT_LENGTH ? styles.charCountOver : ''}`}>
            {text.length}/{MAX_TEXT_LENGTH}
          </div>

          <button
            className={`${styles.keyboardToggle} ${dwellingKey === 'TOGGLE_KB' ? styles.dwelling : ''}`}
            style={dwellingKey === 'TOGGLE_KB' ? { '--dwell-duration': `${dwellDuration}ms` } as React.CSSProperties : undefined}
            onClick={() => setShowKeyboard((prev) => !prev)}
            onPointerEnter={() => startDwell('TOGGLE_KB', () => setShowKeyboard((prev) => !prev))}
            onPointerLeave={cancelDwell}
            type="button"
          >
            {showKeyboard ? t('hideKeyboard') : t('showKeyboard')}
          </button>

          {showKeyboard && (
            <div className={styles.keyboard}>

              <div className={styles.suggestions}>
                {suggestions.map((word) => (
                  <button
                    key={word}
                    className={`${styles.suggestionBtn} ${dwellingKey === `SUG_${word}` ? styles.dwelling : ''}`}
                    style={dwellingKey === `SUG_${word}` ? { '--dwell-duration': `${dwellDuration}ms` } as React.CSSProperties : undefined}
                    onClick={() => handleSelectSuggestion(word)}
                    onPointerEnter={() => startDwell(`SUG_${word}`, () => handleSelectSuggestion(word))}
                    onPointerLeave={cancelDwell}
                    type="button"
                  >
                    {word}
                  </button>
                ))}
              </div>

              {keyboardRows.map((row, i) => (
                <div key={i} className={styles.keyboardRow}>
                  {row.map((key) => (
                    <button
                      key={key}
                      className={`${styles.key} ${dwellingKey === key ? styles.dwelling : ''}`}
                      style={dwellingKey === key ? { '--dwell-duration': `${dwellDuration}ms` } as React.CSSProperties : undefined}
                      onClick={() => handleKeyPress(key)}
                      onPointerEnter={() => startDwell(key, () => handleKeyPress(key))}
                      onPointerLeave={cancelDwell}
                      type="button"
                    >
                      {key}
                    </button>
                  ))}
                </div>
              ))}
              <div className={styles.keyboardRow}>
                {['.', ',', '?', '!'].map((char) => (
                  <button
                    key={char}
                    className={`${styles.key} ${styles.keyPunctuation} ${dwellingKey === char ? styles.dwelling : ''}`}
                    style={dwellingKey === char ? { '--dwell-duration': `${dwellDuration}ms` } as React.CSSProperties : undefined}
                    onClick={() => handlePunctuation(char)}
                    onPointerEnter={() => startDwell(char, () => handlePunctuation(char))}
                    onPointerLeave={cancelDwell}
                    type="button"
                  >
                    {char}
                  </button>
                ))}
                <button
                  className={`${styles.key} ${styles.keySpace} ${dwellingKey === 'SPACE' ? styles.dwelling : ''}`}
                  style={dwellingKey === 'SPACE' ? { '--dwell-duration': `${dwellDuration}ms` } as React.CSSProperties : undefined}
                  onClick={handleSpace}
                  onPointerEnter={() => startDwell('SPACE', handleSpace)}
                  onPointerLeave={cancelDwell}
                  type="button"
                >
                  ␣
                </button>
                <button
                  className={`${styles.key} ${styles.keyClear} ${dwellingKey === 'CLEAR' ? styles.dwelling : ''}`}
                  style={dwellingKey === 'CLEAR' ? { '--dwell-duration': `${dwellDuration}ms` } as React.CSSProperties : undefined}
                  onClick={handleClear}
                  onPointerEnter={() => startDwell('CLEAR', handleClear)}
                  onPointerLeave={cancelDwell}
                  type="button"
                >
                  {t('clear')}
                </button>
                <button
                  className={`${styles.key} ${styles.keyBackspace} ${dwellingKey === 'BACKSPACE' ? styles.dwelling : ''}`}
                  style={dwellingKey === 'BACKSPACE' ? { '--dwell-duration': `${dwellDuration}ms` } as React.CSSProperties : undefined}
                  onClick={handleBackspace}
                  onPointerEnter={() => startDwell('BACKSPACE', handleBackspace)}
                  onPointerLeave={cancelDwell}
                  type="button"
                >
                  ⌫
                </button>
              </div>
            </div>
          )}

          <div className={styles.actions}>
            <div
              className={`${styles.dwellWrap} ${styles.dwellWrapFilled} ${dwellingKey === 'GENERATE' ? styles.dwelling : ''}`}
              style={dwellingKey === 'GENERATE' ? { '--dwell-duration': `${dwellDuration}ms` } as React.CSSProperties : undefined}
              onPointerEnter={() => !(isGenerating || !text.trim() || !TTS_FUNCTION_URL || !modelId) && startDwell('GENERATE', handleGenerate)}
              onPointerLeave={cancelDwell}
            >
              <Button
                type={BUTTON_TYPE.PRIMARY}
                mode={BUTTON_MODE.BASIC}
                onClick={handleGenerate}
                disabled={isGenerating || !text.trim()}
                fullWidth
              >
                {t('generateAudio')}
              </Button>
            </div>

            {isPlaying && !playingSavedId && (
              <div
                className={`${styles.dwellWrap} ${styles.dwellWrapFilled} ${dwellingKey === 'STOP' ? styles.dwelling : ''}`}
                style={dwellingKey === 'STOP' ? { '--dwell-duration': `${dwellDuration}ms` } as React.CSSProperties : undefined}
                onPointerEnter={() => startDwell('STOP', handleStop)}
                onPointerLeave={cancelDwell}
              >
                <Button
                  type={BUTTON_TYPE.ERROR}
                  mode={BUTTON_MODE.BASIC}
                  onClick={handleStop}
                  fullWidth
                >
                  {t('stop')}
                </Button>
              </div>
            )}

            {hasAudio && !isPlaying && !isGenerating && !playingSavedId && (
              <div
                className={`${styles.dwellWrap} ${dwellingKey === 'REPLAY' ? styles.dwelling : ''}`}
                style={dwellingKey === 'REPLAY' ? { '--dwell-duration': `${dwellDuration}ms` } as React.CSSProperties : undefined}
                onPointerEnter={() => startDwell('REPLAY', handleReplay)}
                onPointerLeave={cancelDwell}
              >
                <Button
                  type={BUTTON_TYPE.PRIMARY}
                  mode={BUTTON_MODE.OUTLINE}
                  onClick={handleReplay}
                  fullWidth
                >
                  {t('replay')}
                </Button>
              </div>
            )}

            {hasAudio && !isGenerating && lastBlobRef.current && (
              <div
                className={`${styles.dwellWrap} ${dwellingKey === 'SAVE' ? styles.dwelling : ''}`}
                style={dwellingKey === 'SAVE' ? { '--dwell-duration': `${dwellDuration}ms` } as React.CSSProperties : undefined}
                onPointerEnter={() => !(isSaving || savedAudios.length >= MAX_SAVED_AUDIOS) && startDwell('SAVE', handleSave)}
                onPointerLeave={cancelDwell}
              >
                <Button
                  type={BUTTON_TYPE.PRIMARY}
                  mode={BUTTON_MODE.OUTLINE}
                  onClick={handleSave}
                  disabled={isSaving || savedAudios.length >= MAX_SAVED_AUDIOS}
                  fullWidth
                >
                  {isSaving ? t('saving') : t('save')}
                </Button>
              </div>
            )}

            {hasAudio && !isGenerating && lastBlobRef.current && (
              <div
                className={`${styles.dwellWrap} ${dwellingKey === 'SHARE' ? styles.dwelling : ''}`}
                style={dwellingKey === 'SHARE' ? { '--dwell-duration': `${dwellDuration}ms` } as React.CSSProperties : undefined}
                onPointerEnter={() => startDwell('SHARE', handleDownload)}
                onPointerLeave={cancelDwell}
              >
                <Button
                  ref={shareBtnRef}
                  type={BUTTON_TYPE.PRIMARY}
                  mode={BUTTON_MODE.OUTLINE}
                  onClick={handleShare}
                  fullWidth
                >
                  {t('share')}
                </Button>
              </div>
            )}

            {hasAudio && !isGenerating && lastBlobRef.current && (
              <div
                className={`${styles.dwellWrap} ${styles.dwellWrapSmall} ${dwellingKey === 'DOWNLOAD' ? styles.dwelling : ''}`}
                style={dwellingKey === 'DOWNLOAD' ? { '--dwell-duration': `${dwellDuration}ms` } as React.CSSProperties : undefined}
                onPointerEnter={() => startDwell('DOWNLOAD', handleDownload)}
                onPointerLeave={cancelDwell}
              >
                <button
                  className={styles.downloadBtn}
                  onClick={handleDownload}
                  aria-label={t('download')}
                >
                  <FeatherIcon icon="download" size="22" />
                </button>
              </div>
            )}
          </div>

          {savedAudios.length >= MAX_SAVED_AUDIOS && hasAudio && (
            <p className={styles.errorMessage}>{t('maxSaved')}</p>
          )}

          {isGenerating && (
            <p className={styles.loadingMessage}>{t('loading')}</p>
          )}

          {error && <p className={styles.errorMessage}>{error}</p>}

          <button
            className={`${styles.settingsToggle} ${dwellingKey === 'TOGGLE_SETTINGS' ? styles.dwelling : ''}`}
            style={dwellingKey === 'TOGGLE_SETTINGS' ? { '--dwell-duration': `${dwellDuration}ms` } as React.CSSProperties : undefined}
            onClick={() => setShowSettings((prev) => !prev)}
            onPointerEnter={() => startDwell('TOGGLE_SETTINGS', () => setShowSettings((prev) => !prev))}
            onPointerLeave={cancelDwell}
            type="button"
          >
            {showSettings ? t('hideSettings') : t('showSettings')}
          </button>

          {showSettings && (
            <div className={styles.settingsPanel}>
              <div className={styles.dwellSettings}>
                <button
                  className={`${styles.dwellToggle} ${dwellEnabled ? styles.dwellToggleOn : ''}`}
                  onClick={handleDwellToggle}
                  type="button"
                >
                  {t('dwellMode')}: {dwellEnabled ? t('dwellOn') : t('dwellOff')}
                </button>
                {dwellEnabled && (
                  <div className={styles.dwellSlider}>
                    <label className={styles.dwellLabel}>
                      {t('dwellDuration')}: {dwellDuration}ms
                    </label>
                    <input
                      type="range"
                      min={400}
                      max={2000}
                      step={100}
                      value={dwellDuration}
                      onChange={(e) => handleDwellDurationChange(Number(e.target.value))}
                      className={styles.dwellRange}
                    />
                  </div>
                )}
              </div>

              <div className={styles.speedControl}>
                <div className={styles.speedHeader}>
                  <label className={styles.speedLabel}>{t('speed')}</label>
                  <span className={styles.speedValue}>{speed}x</span>
                </div>
                <input
                  className={styles.speedSlider}
                  type="range"
                  min={SPEED_MIN}
                  max={SPEED_MAX}
                  step={SPEED_STEP}
                  value={speed}
                  onChange={(e) => handleSpeedChange(Math.round(Number(e.target.value) * 10) / 10)}
                  disabled={isGenerating}
                />
                <div className={styles.speedRange}>
                  <span>{SPEED_MIN}x</span>
                  <span>{SPEED_MAX}x</span>
                </div>
              </div>
            </div>
          )}

          <div className={styles.savedSection}>
            <h4 className={styles.savedTitle}>{t('savedAudios')}</h4>
            {savedAudios.length === 0 ? (
              <p className={styles.noSaved}>{t('noSavedAudios')}</p>
            ) : (
              <ul className={styles.savedList}>
                {savedAudios.map((audio) => (
                  <li key={audio.id} className={styles.savedItem}>
                    <span className={styles.savedLabel}>{audio.label}</span>
                    <div className={styles.savedActions}>
                      <button
                        className={`${styles.savedBtn} ${styles.playBtn} ${
                          playingSavedId === audio.id && isPlaying
                            ? styles.activeBtn
                            : ''
                        } ${dwellingKey === `PLAY_${audio.id}` ? styles.dwelling : ''}`}
                        style={dwellingKey === `PLAY_${audio.id}` ? { '--dwell-duration': `${dwellDuration}ms` } as React.CSSProperties : undefined}
                        onClick={() => handlePlaySaved(audio)}
                        onPointerEnter={() => startDwell(`PLAY_${audio.id}`, () => handlePlaySaved(audio))}
                        onPointerLeave={cancelDwell}
                        aria-label={playingSavedId === audio.id && isPlaying ? t('stop') : t('replay')}
                        type="button"
                      >
                        {playingSavedId === audio.id && isPlaying
                          ? t('stop')
                          : '▶'}
                      </button>
                      <button
                        className={`${styles.savedBtn} ${styles.deleteBtn} ${dwellingKey === `DEL_${audio.id}` ? styles.dwelling : ''}`}
                        style={dwellingKey === `DEL_${audio.id}` ? { '--dwell-duration': `${dwellDuration}ms` } as React.CSSProperties : undefined}
                        onClick={() => handleDeleteSaved(audio)}
                        onPointerEnter={() => startDwell(`DEL_${audio.id}`, () => handleDeleteSaved(audio))}
                        onPointerLeave={cancelDwell}
                        type="button"
                      >
                        {t('delete')}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <audio ref={audioRef} onEnded={handleAudioEnded} />
        </div>
      </div>

      {dwellEnabled && (
        <div className={styles.scrollButtons}>
          <button
            className={`${styles.scrollBtn} ${dwellingKey === 'SCROLL_UP' ? styles.dwelling : ''}`}
            style={dwellingKey === 'SCROLL_UP' ? { '--dwell-duration': `${dwellDuration}ms` } as React.CSSProperties : undefined}
            onPointerEnter={() => startDwell('SCROLL_UP', () => startContinuousScroll('up'))}
            onPointerLeave={() => { cancelDwell(); stopContinuousScroll(); }}
            type="button"
            aria-label="Scroll up"
          >
            ▲
          </button>
          <button
            className={`${styles.scrollBtn} ${dwellingKey === 'SCROLL_DOWN' ? styles.dwelling : ''}`}
            style={dwellingKey === 'SCROLL_DOWN' ? { '--dwell-duration': `${dwellDuration}ms` } as React.CSSProperties : undefined}
            onPointerEnter={() => startDwell('SCROLL_DOWN', () => startContinuousScroll('down'))}
            onPointerLeave={() => { cancelDwell(); stopContinuousScroll(); }}
            type="button"
            aria-label="Scroll down"
          >
            ▼
          </button>
        </div>
      )}
    </>
  );
}
